import { useEffect, useRef, useState } from "react";
import { Modal } from "@clarkinc/zest-design-system/ui";
import { cls } from "cls";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import "~/less/styles-ws-plus-modal.less";
import {
  EnrollmentOption,
  PlusPotentialSavingsInfo,
  RestorableSubscription,
  SavedCardData,
  ShippingAddress,
} from "../models";
import PlusSubscriptionModalStore, {
  ModalTypes,
} from "../PlusSubscriptionModalStore";
import Header from "./Header";
import {
  initialState as initialModalState,
  ModalState,
} from "./FormContent/variants/modalState";
import {
  RestoreSubscriptionForm,
  StandardSubscriptionForm,
} from "./FormContent";

interface Props {
  userGuid: string | undefined;
  shippingAddresses: readonly ShippingAddress[];
  defaultShippingAddressId: number;
  formattedPlusPrice: string;
  savedCardData: SavedCardData[] | readonly SavedCardData[];
  enrollmentOptions: EnrollmentOption[] | readonly EnrollmentOption[];
  enrollment: EnrollmentOption | undefined;
  addressPlusSignup: boolean;
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined;
  subscriptionChargeDate?: Date;
  isOpenFromAd?: boolean;
  showRestoreForm?: boolean;
  isPendingCancellation?: boolean;
  restorableSubscriptions: RestorableSubscription[];
}

const PlusSubscriptionModal = ({
  userGuid,
  shippingAddresses,
  defaultShippingAddressId,
  formattedPlusPrice,
  savedCardData,
  enrollmentOptions,
  enrollment,
  addressPlusSignup,
  plusPotentialSavingsInfo,
  subscriptionChargeDate,
  isOpenFromAd = false,
  showRestoreForm = false,
  isPendingCancellation = false,
  restorableSubscriptions,
}: Props) => {
  const plusModalId = "plus-subscription-modal";
  const plusSubscriptionModalOpen = PlusSubscriptionModalStore.useState(
    (state) => state.modalType === ModalTypes.PLUS,
  );

  const defaultCard =
    savedCardData !== undefined
      ? (savedCardData.filter((element) => element.default)[0] ??
        savedCardData[0])
      : undefined;

  const [modalState, setModalState] = useState<ModalState>({
    ...initialModalState,
    promotionName: enrollment ? enrollment.promotionName : undefined,
    shippingId: defaultShippingAddressId,
    savedCardId: defaultCard?.num,
    savedCardData: savedCardData ?? [],
  });

  const setPlusSubscriptionModalOpen =
    PlusSubscriptionModalStore.getState().setModalOpen;

  const onOpenChange = (open: boolean) => {
    setModalState((prev) => ({
      ...prev,
      welcomeMessageIsVisible: false,
    }));

    if (open) {
      setPlusSubscriptionModalOpen(ModalTypes.PLUS);
    } else {
      setPlusSubscriptionModalOpen(ModalTypes.CLOSED);
    }
  };

  const handleModalClose = () => {
    setModalState((prev) => ({
      ...prev,
      cardVerificationError: false,
      cardSaveError: false,
      subscribeError: false,
      subscribeCustomErrorMessage: "",
      marketingOptIn: true,
      shippingId: defaultShippingAddressId,
      welcomeMessageIsVisible: false,
    }));

    setPlusSubscriptionModalOpen(ModalTypes.CLOSED);

    if (modalState.onCloseUrlUpdate) {
      setWindowLocationHref(modalState.onCloseUrlUpdate);
    }
  };

  const modalRef = useRef<HTMLDivElement>(null);

  const handleShowWelcome = () => {
    if (!modalRef.current) return;

    const el = modalRef.current;

    setModalState((prev) => ({ ...prev, welcomeMessageIsVisible: true }));

    el.scrollTop = 0;

    setTimeout(() => {
      el.style.overflow = "hidden";
      el.style.background = "#022E44";
    }, 500);
  };

  useEffect(() => {
    const dialog = document.querySelector(
      "[aria-label='Plus Subscription Modal']",
    );
    if (plusSubscriptionModalOpen && dialog?.getAttribute("open") === null) {
      setPlusSubscriptionModalOpen(ModalTypes.PLUS);
    }
  }, [setPlusSubscriptionModalOpen, plusSubscriptionModalOpen]);

  useEffect(() => {
    if (modalState.shippingId === 0) {
      if (
        showRestoreForm &&
        restorableSubscriptions?.length > 0 &&
        restorableSubscriptions[0]?.address.id
      ) {
        setModalState((prev) => ({
          ...prev,
          shippingId: restorableSubscriptions[0]!.address.id,
        }));
      } else if (defaultShippingAddressId != 0) {
        setModalState((prev) => ({
          ...prev,
          shippingId: defaultShippingAddressId,
        }));
      }
      if ((shippingAddresses?.length ?? 0) > 0) {
        setModalState((prev) => ({
          ...prev,
          shippingId: shippingAddresses.find(() => true)?.id ?? 0,
        }));
      }
    }
  }, [
    defaultShippingAddressId,
    modalState.shippingId,
    restorableSubscriptions,
    shippingAddresses,
    showRestoreForm,
  ]);

  useEffect(() => {
    if (enrollment?.promotionName) {
      setModalState((prev) => ({
        ...prev,
        promotionName: enrollment.promotionName,
      }));
    }
  }, [enrollment]);

  useEffect(() => {
    if (!plusSubscriptionModalOpen) return;

    const modalContainer = modalRef.current;
    if (modalContainer) {
      modalContainer.setAttribute("tabindex", "-1");
      modalContainer.focus();
      modalContainer.removeAttribute("tabindex");
    }
  }, [plusSubscriptionModalOpen]);

  return (
    <Modal
      ref={modalRef}
      theme="wss"
      open={plusSubscriptionModalOpen}
      onOpenChange={onOpenChange}
      aria-label="Plus Subscription Modal"
      data-testid="plus-subscription-modal"
      id={plusModalId}
      className="w-full max-w-[579px] xxm:rounded-2xl lm:max-w-[959px] xl:max-w-[1123px] overflow-auto scrollbar-width-none"
    >
      <div className="m-0 min-h-screen">
        <div className="relative w-full lm:h-full overflow-hidden flex flex-col-reverse lm:flex-row">
          {/* LEFT SIDE: Form Section */}
          <div
            className={cls(
              "relative transition-[width,height] duration-[300ms] ease-in-out overflow-hidden",
              modalState.welcomeMessageIsVisible
                ? "lm:w-0" // desktop: collapse width, mobile: collapse height
                : "h-auto lm:w-1/2 xl:w-[489px]",
            )}
          >
            {showRestoreForm && restorableSubscriptions?.length > 0 ? (
              <RestoreSubscriptionForm
                modalState={modalState}
                setModalState={setModalState}
                formattedPlusPrice={formattedPlusPrice}
                enrollmentOptions={enrollmentOptions}
                address={restorableSubscriptions[0]!.address}
                handleShowWelcome={handleShowWelcome}
                plusPotentialSavingsInfo={plusPotentialSavingsInfo}
                isOpenFromAd={isOpenFromAd}
                subscriptionChargeDate={restorableSubscriptions[0]!.renewalDate}
                isPendingCancellation={isPendingCancellation}
              />
            ) : (
              <StandardSubscriptionForm
                modalState={modalState}
                setModalState={setModalState}
                userGuid={userGuid}
                enrollmentOptions={enrollmentOptions}
                shippingAddresses={shippingAddresses}
                formattedPlusPrice={formattedPlusPrice}
                addressPlusSignup={addressPlusSignup}
                subscriptionChargeDate={subscriptionChargeDate}
                isOpenFromAd={isOpenFromAd}
                plusPotentialSavingsInfo={plusPotentialSavingsInfo}
                handleShowWelcome={handleShowWelcome}
              />
            )}
          </div>
          {/* RIGHT SIDE: Testimony / Welcome Message */}
          <div
            className={cls(
              "transition-[width,height] duration-[300ms] ease-in-out overflow-hidden",
              modalState.welcomeMessageIsVisible
                ? "absolute inset-0 lm:relative h-full lm:w-full" // desktop: expand width, mobile: expand height
                : "h-[10%] lm:w-1/2 lm:h-full xl:w-[634px]",
            )}
          >
            <Header
              onClose={handleModalClose}
              welcomeMessageIsVisible={modalState.welcomeMessageIsVisible}
              modalId={plusModalId}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlusSubscriptionModal;
